import React, { useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { InputKlarnaCheckoutConfig } from "lib/types/generated/graphql-types"
import { useLazyQuery, useMutation } from "@apollo/client"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { useAppDispatch } from "lib/store"
import alertActions from "lib/store/services/Alert/AlertSlice"

import CREATE_OR_UPDATE_KUSTOM_CONFIG from "../../../../graphql/mutations/config/CreateOrUpdateKustomConfig"
import VALIDATE_KUSTOM from "graphql/queries/config/ValidateKustomConfig"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import { KustomCheckoutConfigInputs } from "components/Config/KustomCheckout/KustomCheckoutConfigInputs"
import Popup from "components/Ui/Popup"

type Props = {
  refetch: () => void
}

interface ExtendedInputKlarnaConfig extends InputKlarnaCheckoutConfig {
  id: string
}

function CreateKustomConfig({ refetch }: Props) {
  const dispatch = useAppDispatch()
  const [validationError, setValidationError] = useState<boolean>(false)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<ExtendedInputKlarnaConfig>({ mode: "onBlur" })

  const [addKlarnaConfig, { loading: createLoading }] = useMutation(
    CREATE_OR_UPDATE_KUSTOM_CONFIG,
    {
      onCompleted: () => {
        reset({
          id: "",
          baseUrl: "",
          merchantId: "",
          password: "",
          username: "",
          klarnaShippingAssistantEnabled: false
        })
        dispatch(hideEditSidebar())
        dispatch(
          alertActions.actions.createAlert({
            message: "Kustom checkout configuration successfully created",
            type: "success"
          })
        )
        refetch()
      },
      onError: () => {
        dispatch(
          alertActions.actions.createAlert({
            type: "error"
          })
        )
      }
    }
  )
  const [validateKlarna, { loading }] = useLazyQuery(VALIDATE_KUSTOM)

  const onSubmit: SubmitHandler<ExtendedInputKlarnaConfig> = (data) => {
    validateKlarna({
      variables: {
        baseUrl: data.baseUrl,
        username: data.username,
        password: data.password
      }
    }).then((response) => {
      if (response.data.Configuration.validate.klarnaCheckout.valid) {
        addKlarnaConfig({ variables: { ...data } })
      } else {
        setValidationError(true)
      }
    })
  }

  const directSubmit: SubmitHandler<ExtendedInputKlarnaConfig> = (data) => {
    addKlarnaConfig({ variables: { ...data } })
    setValidationError(false)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <EditSidebar>
        <EditSidebarHeader title="Create Kustom checkout configuration">
          {validationError ? (
            <Popup
              title="Validation error"
              subtitle="Couldn't validate provided credentials with Kustom. Do you want to save the Kustom checkout config anyway? "
              buttonText="Yes, save"
              handleOkClick={handleSubmit(directSubmit)}
              handleCloseClick={() => setValidationError(false)}
            />
          ) : (
            <></>
          )}
          <PrimaryButton type="submit" loading={createLoading || loading}>
            Save
          </PrimaryButton>
        </EditSidebarHeader>
        <KustomCheckoutConfigInputs
          control={control}
          errors={errors}
          setValue={setValue}
          showId={true}
        />
      </EditSidebar>
    </form>
  )
}

export default CreateKustomConfig
